import type { ReactElement } from 'react'

import { AriaLoadingStatusRegion } from '@monthio/design-system'
import { flowAnimationService } from '@monthio/react-components'
import config from '#config'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import { BuildingBlockAnimation } from './components/LoadingAnimation'

const ScrollableWrapper = styled.div`
	height: 100%;
	overflow: hidden;
	display: flex;
	flex-direction: column;

	${(props) => props.theme.breakpoints.up('sm')} {
		width: 100%;
		flex-grow: 1;
		overflow: auto;
	}
`

const Wrapper = styled.div`
	overflow: auto;
	height: 100%;
	width: 100%;
	margin: auto;
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	position: relative;
`

export const AppContentWithAnimation = ({
	children,
}: {
	children: React.ReactNode
}): ReactElement => {
	const history = useHistory()

	const [showAnimation, setShowAnimation] = useState(false)
	const [showTransactionProgressBar, setShowTransactionProgressBar] =
		useState(false)
	const [loadingStatus, setLoadingStatus] = useState('ready')

	useEffect(() => {
		if (!showAnimation) {
			setLoadingStatus('ready')
		}
	}, [showAnimation])

	useEffect(() => {
		return flowAnimationService.subscribe((showAnimation) => {
			setShowAnimation(showAnimation)
		})
	}, [])

	useEffect(() => {
		const routesWithAnimation = [
			config.routeCodes.bankTransactionFetchingPage,
			config.routeCodes.miniBudget,
			config.routeCodes.debtOverview,
			config.routeCodes.assetOverview,
			config.routeCodes.assetExpenses,
			config.routeCodes.incomeVerification,
		] as string[]

		if (
			showAnimation &&
			!routesWithAnimation.includes(history.location.pathname)
		) {
			setShowAnimation(false)
		}

		// Avoid removing the progress bar in the middle of loading by only turning it on here (not off)
		const isTransactionFetchingPage =
			history.location.pathname ===
			config.routeCodes.bankTransactionFetchingPage

		if (!showTransactionProgressBar && isTransactionFetchingPage) {
			setShowTransactionProgressBar(true)
		}
	}, [history.location.pathname, showAnimation, showTransactionProgressBar])

	return (
		<>
			<AriaLoadingStatusRegion status={loadingStatus} />
			{showAnimation ? (
				<BuildingBlockAnimation
					showTransactionFetchingProgress={showTransactionProgressBar}
				/>
			) : null}
			<ScrollableWrapper>
				<Wrapper>{children}</Wrapper>
			</ScrollableWrapper>
		</>
	)
}
